import { useEffect, useMemo, useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { formatDate, formatDateTime } from '@/lib/format'
import { useSearchParams } from 'react-router-dom'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Separator } from '@/components/ui/separator'
import { Skeleton } from '@/components/ui/skeleton'
import { Textarea } from '@/components/ui/textarea'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'
import { NumericInput } from '@/components/ui/numeric-input'
import { Mail, Link2, Unlink, ExternalLink, RefreshCw, Pen, Check, Loader2, Users, UserPlus, Trash2, CalendarClock, Gauge, AlertTriangle, MailWarning } from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  useEmailAccounts,
  useDisconnectEmailAccount,
  useUpdateEmailAccount,
  useCheckSenderDomain,
  useHubSpotStatus,
  useDisconnectHubSpot,
  useAccountHealth,
  useAvailableProviders,
  useHubSpotAvailableProperties,
  useSyncPropertiesConfig,
  useUpdateSyncProperties,
  useTeamMembers,
  useInvitations,
  useCreateInvitation,
  useCancelInvitation,
  useRemoveMember,
  useUpdateMemberRole,
  useOrganization,
  useUpdateOrganization,
} from '@/hooks/useApi'
import type { HubSpotPropertyInfo } from '@/hooks/useApi'
import { useToast } from '@/hooks/use-toast'
import { getOAuthErrorMessage, getOAuthSuccessMessage } from '@/lib/oauth-errors'
import type { AccountHealthStatus, DnsRecordStatus, EmailAccount } from '@/types/api'

const API_BASE = import.meta.env.VITE_API_URL || ''

const HUBSPOT_ICON_PATH = "M17.09 2.82c-.75-.3-1.59-.46-2.45-.46-3.93 0-7.12 3.19-7.12 7.12 0 .86.15 1.7.46 2.45L2.82 17.09c-.39.39-.61.92-.61 1.48 0 1.15.94 2.09 2.09 2.09.56 0 1.09-.22 1.48-.61l5.16-5.16c.75.3 1.59.46 2.45.46 3.93 0 7.12-3.19 7.12-7.12 0-.86-.15-1.7-.46-2.45l-3.96 3.96L12.91 6l3.96-3.96.22-.22Z"

function HubSpotIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24">
      <path fill="currentColor" d={HUBSPOT_ICON_PATH} />
    </svg>
  )
}

function isHealthBad(status: AccountHealthStatus | undefined): boolean {
  return status === 'expired' || status === 'refresh_failed'
}

/**
 * The daily send limit of an email account (#370): what it has sent against
 * it, and the field to lower it. The limit never goes above the maximum of
 * the provider. Shows nothing with an API that does not know the limit yet.
 */
function AccountDailyLimit({ account }: { account: EmailAccount }) {
  const { t } = useTranslation('settings')
  const { toast } = useToast()
  const updateEmailAccount = useUpdateEmailAccount()
  // The text of the limit as typed; null while the field is closed
  const [draft, setDraft] = useState<string | null>(null)

  const maximum = account.max_daily_send_limit
  const today = account.daily_limit_today
  if (maximum === undefined || today === undefined) return null

  const limit = account.daily_send_limit ?? maximum
  const inputId = `daily-limit-${account.id}`
  const invalidId = `${inputId}-invalid`
  // Only what is written in the field can be saved: a whole number from 1 to
  // the maximum of the provider. An empty field is not the maximum, it is
  // nothing to save.
  const typed = draft !== null && /^[1-9][0-9]*$/.test(draft) ? Number(draft) : null
  const valid = typed !== null && typed <= maximum

  const save = () => {
    if (!valid) return
    updateEmailAccount.mutate(
      // At the maximum the account has no limit of its own
      { id: account.id, daily_send_limit: typed === maximum ? null : typed },
      {
        onSuccess: () => {
          setDraft(null)
          toast({ title: t('dailyLimit.saved') })
        },
        onError: () => {
          toast({ variant: 'destructive', title: t('dailyLimit.saveError') })
        },
      },
    )
  }

  return (
    <div className="px-4 pb-4 space-y-2">
      <div className="flex items-center justify-between gap-3 text-sm text-muted-foreground">
        <div>
          <p>
            {account.sent_today == null
              ? t('dailyLimit.limitOnly', { limit: today })
              : t('dailyLimit.sentToday', { sent: account.sent_today, limit: today })}
          </p>
          {account.ramping_up && (
            <p className="text-xs">{t('dailyLimit.rampingUp', { today, limit })}</p>
          )}
        </div>
        {draft === null && (
          <Button variant="ghost" size="sm" onClick={() => setDraft(String(limit))}>
            <Gauge className="h-4 w-4 mr-2" />
            {t('dailyLimit.edit')}
          </Button>
        )}
      </div>
      {draft !== null && (
        <div className="space-y-2">
          <Label htmlFor={inputId}>{t('dailyLimit.label')}</Label>
          <div className="flex items-center gap-2">
            <NumericInput
              id={inputId}
              className="w-24"
              // The typed text is the state: the field starts from the
              // limit of the account and reports every keystroke
              value={limit}
              min={1}
              max={maximum}
              onChange={() => {}}
              onTextChange={setDraft}
              aria-invalid={!valid}
              aria-describedby={valid ? undefined : invalidId}
            />
            <Button size="sm" onClick={save} disabled={!valid || updateEmailAccount.isPending}>
              {updateEmailAccount.isPending ? (
                <Loader2 className="h-4 w-4 mr-1 animate-spin" />
              ) : (
                <Check className="h-4 w-4 mr-1" />
              )}
              {t('dailyLimit.save')}
            </Button>
            <Button variant="outline" size="sm" onClick={() => setDraft(null)}>
              {t('dailyLimit.cancel')}
            </Button>
          </div>
          {!valid && (
            <p id={invalidId} role="alert" className="text-xs text-destructive">
              {t('dailyLimit.invalid', { max: maximum })}
            </p>
          )}
          <p className="text-xs text-muted-foreground">
            {t('dailyLimit.hint', { max: maximum })}
          </p>
        </div>
      )}
    </div>
  )
}

const DNS_RECORDS = ['spf', 'dkim', 'dmarc', 'mx'] as const

const DNS_STATUS_BADGE: Record<DnsRecordStatus, 'secondary' | 'destructive' | 'outline'> = {
  present: 'secondary',
  missing: 'destructive',
  unverifiable: 'outline',
}

/**
 * The DNS records of the domain an account sends from (#371): SPF, DKIM,
 * DMARC and MX as last read, with a button to read them again. A missing
 * record is a warning in plain words; one that could not be read is not.
 * It is information only: nothing here pauses the account or its sends.
 * Shows nothing for a personal address, which has no domain to check.
 */
function AccountSenderDomain({ account }: { account: EmailAccount }) {
  const { t } = useTranslation('settings')
  const { toast } = useToast()
  const checkSenderDomain = useCheckSenderDomain()

  const check = account.sender_domain
  if (!check) return null

  const missing = DNS_RECORDS.filter((record) => check[record] === 'missing')
  const dkimUnverifiable = check.dkim === 'unverifiable'
  const othersUnverifiable = DNS_RECORDS.some(
    (record) => record !== 'dkim' && check[record] === 'unverifiable',
  )

  const recheck = () => {
    checkSenderDomain.mutate(account.id, {
      onSuccess: () => toast({ title: t('senderDomain.rechecked') }),
      onError: () => toast({ variant: 'destructive', title: t('senderDomain.recheckError') }),
    })
  }

  return (
    <div className="px-4 pb-4 space-y-2">
      <div className="flex items-center justify-between gap-3 text-sm text-muted-foreground">
        <div className="space-y-1">
          <p>{t('senderDomain.title', { domain: check.domain })}</p>
          {check.checked_at && (
            <div className="flex flex-wrap gap-1">
              {DNS_RECORDS.map((record) => {
                const status = check[record]
                if (!status) return null
                return (
                  <Badge key={record} variant={DNS_STATUS_BADGE[status]} className="text-xs">
                    {t('senderDomain.record', {
                      record: record.toUpperCase(),
                      status: t(`senderDomain.status.${status}`),
                    })}
                  </Badge>
                )
              })}
            </div>
          )}
          <p className="text-xs">
            {check.checked_at
              ? t('senderDomain.checkedAt', { date: formatDateTime(check.checked_at) })
              : t('senderDomain.neverChecked')}
          </p>
        </div>
        <Button variant="ghost" size="sm" onClick={recheck} disabled={checkSenderDomain.isPending}>
          {checkSenderDomain.isPending ? (
            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
          ) : (
            <RefreshCw className="h-4 w-4 mr-2" />
          )}
          {t('senderDomain.recheck')}
        </Button>
      </div>
      {missing.length > 0 && (
        <div
          role="alert"
          className="flex gap-2 rounded-md border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900 dark:border-amber-800 dark:bg-amber-950 dark:text-amber-200"
        >
          <AlertTriangle className="h-4 w-4 shrink-0" />
          <div className="space-y-1">
            <p>{t('senderDomain.missing.intro')}</p>
            <ul className="list-disc pl-4 space-y-1">
              {missing.map((record) => (
                <li key={record}>{t(`senderDomain.missing.${record}`)}</li>
              ))}
            </ul>
            <p>{t('senderDomain.missing.action')}</p>
          </div>
        </div>
      )}
      {dkimUnverifiable && (
        <p className="text-xs text-muted-foreground">{t('senderDomain.dkimUnverifiable')}</p>
      )}
      {othersUnverifiable && (
        <p className="text-xs text-muted-foreground">{t('senderDomain.unverifiable')}</p>
      )}
    </div>
  )
}

export function Settings() {
  const { t } = useTranslation('settings')
  const [searchParams] = useSearchParams()
  const { toast } = useToast()

  const { data: emailAccounts, isLoading: loadingAccounts } = useEmailAccounts()
  const { data: hubspotStatus, isLoading: loadingHubspot } = useHubSpotStatus()
  const { data: health } = useAccountHealth()
  const { data: providersData } = useAvailableProviders()
  const updateEmailAccount = useUpdateEmailAccount()
  const [editingSignature, setEditingSignature] = useState<string | null>(null)
  const [signatureDraft, setSignatureDraft] = useState('')
  const disconnectEmail = useDisconnectEmailAccount()
  const disconnectHubspot = useDisconnectHubSpot()
  const userStr = localStorage.getItem('user')
  const currentUserProfile = userStr ? JSON.parse(userStr) : null
  const isAdmin = currentUserProfile?.role === 'admin'

  // Show all buttons while loading (optimistic), hide only when we know a provider is disabled
  const providersLoaded = !!providersData
  const isProviderAvailable = (p: string) => !providersLoaded || (providersData?.providers ?? []).includes(p)

  // Index health by account ID for O(1) lookups
  const healthByAccountId = useMemo(
    () => new Map(health?.email_accounts?.map(h => [h.id, h]) ?? []),
    [health],
  )

  const hubspotBad = isHealthBad(health?.hubspot?.status)
  const hubspotScopeBad = health?.hubspot && !health.hubspot.scopes_ok && health.hubspot.status === 'valid'

  // Parse OAuth callback query params on mount
  useEffect(() => {
    const error = searchParams.get('error')
    const success = searchParams.get('success')
    const hubspot = searchParams.get('hubspot')
    const detail = searchParams.get('detail')
    const reason = searchParams.get('reason')

    if (error) {
      toast({
        variant: 'destructive',
        title: t('toast.connectionFailed'),
        description: getOAuthErrorMessage(error),
      })
    } else if (success) {
      toast({
        variant: 'success',
        title: t('toast.accountConnected'),
        description: getOAuthSuccessMessage(success),
      })
    } else if (hubspot === 'connected') {
      toast({
        variant: 'success',
        title: t('toast.hubspotConnected'),
        description: t('toast.hubspotConnectedDescription'),
      })
    } else if (hubspot === 'error' && (reason || detail)) {
      toast({
        variant: 'destructive',
        title: t('toast.hubspotConnectionFailed'),
        // A known reason is localized; otherwise fall back to the raw detail.
        description: reason
          ? t(`toast.hubspotRefused.${reason}`, { defaultValue: detail ?? reason })
          : decodeURIComponent(detail ?? ''),
      })
    }

    // Clean URL to prevent duplicate toasts on refresh
    if (error || success || hubspot) {
      window.history.replaceState({}, '', '/settings')
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps -- intentional: run once on mount to show callback toasts

  const handleDisconnectEmail = (accountId: string, provider: string) => {
    disconnectEmail.mutate(accountId, {
      onSuccess: (data) => {
        if (data.revocation_warning) {
          toast({
            variant: 'warning',
            title: t('toast.accountDisconnected'),
            description: (
              <span>
                {t('toast.tokenNotRevoked', { provider })}
                <a
                  href={data.revocation_warning}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="underline font-medium"
                >
                  {t('toast.revokeManually')}
                </a>
              </span>
            ),
          })
        } else {
          toast({
            variant: 'success',
            title: t('toast.accountDisconnected'),
            description: t('toast.accountRemoved', { provider }),
          })
        }
      },
    })
  }

  const handleDisconnectHubSpot = () => {
    // The disconnect hard-deletes synced contacts, segments and the send history
    // attached to them: irreversible, so it is never one click away.
    if (!confirm(t('hubspot.disconnectPurgeConfirm'))) return
    disconnectHubspot.mutate(undefined, {
      onSuccess: (data) => {
        const deletionSummary =
          data.purged && data.purged.total > 0
            ? t('toast.hubspotDataPurged', {
                contacts: data.purged.contacts,
                segments: data.purged.segments,
              })
            : t('toast.hubspotUnlinked')
        toast({
          variant: data.revocation_warning ? 'warning' : 'success',
          title: t('toast.hubspotDisconnected'),
          description: data.revocation_warning
            ? `${t('toast.hubspotTokenNotRevoked')} ${deletionSummary}`
            : deletionSummary,
        })
      },
    })
  }

  const handleConnectEmail = (provider: 'gmail' | 'microsoft') => {
    const token = localStorage.getItem('access_token')
    window.location.href = `${API_BASE}/api/email-accounts/connect/${provider}?token=${token}`
  }

  const handleConnectHubSpot = async () => {
    try {
      const token = localStorage.getItem('access_token')
      const response = await fetch(`${API_BASE}/api/hubspot/auth`, {
        headers: {
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      })
      const data = await response.json()
      if (data.url) {
        window.location.href = data.url
      }
    } catch {
      toast({
        variant: 'destructive',
        title: t('toast.error'),
        description: t('toast.hubspotConnectStartFailed'),
      })
    }
  }

  return (
    <div>
      <h1 className="text-3xl font-bold mb-2">{t('title')}</h1>
      <p className="text-muted-foreground mb-8">{t('subtitle')}</p>

      <div className="space-y-6">
        {/* Email Accounts */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Mail className="h-5 w-5" />
              {t('emailAccounts.title')}
            </CardTitle>
            <CardDescription>
              {t('emailAccounts.description')}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {loadingAccounts ? (
              <div className="space-y-3">
                <Skeleton className="h-16 w-full" />
                <Skeleton className="h-16 w-full" />
              </div>
            ) : emailAccounts && emailAccounts.length > 0 ? (
              <div className="space-y-3">
                {emailAccounts.map((account) => {
                  const accountHealth = healthByAccountId.get(account.id)
                  const isExpired = isHealthBad(accountHealth?.status)
                  const isScopeBad = accountHealth && !accountHealth.scopes_ok && accountHealth.status === 'valid'

                  return (
                    <div
                      key={account.id}
                      className={`border rounded-lg ${
                        isExpired
                          ? 'border-red-500/50 bg-red-950/20'
                          : isScopeBad
                            ? 'border-yellow-500/50 bg-yellow-950/20'
                            : ''
                      }`}
                    >
                    <div className="flex items-center justify-between p-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-muted flex items-center justify-center">
                          <Mail className="h-5 w-5 text-muted-foreground" />
                        </div>
                        <div>
                          <p className="font-medium">{account.email}</p>
                          <div className="flex items-center gap-2 text-sm text-muted-foreground">
                            <Badge variant="secondary" className="capitalize">
                              {account.provider}
                            </Badge>
                            {accountHealth ? (
                              isExpired ? (
                                <span className="flex items-center gap-1 text-red-500">{t('emailAccounts.status.tokenExpired')}</span>
                              ) : isScopeBad ? (
                                <span className="flex items-center gap-1 text-yellow-500">{t('emailAccounts.status.incompletePermissions')}</span>
                              ) : (
                                <span className="flex items-center gap-1 text-green-500">{t('emailAccounts.status.validPermissionsOk')}</span>
                              )
                            ) : account.is_active ? (
                              <span className="flex items-center gap-1 text-green-600">{t('emailAccounts.status.active')}</span>
                            ) : (
                              <span className="flex items-center gap-1 text-red-600">{t('emailAccounts.status.inactive')}</span>
                            )}
                          </div>
                          {(isExpired || isScopeBad) && (
                            <Button
                              variant="destructive"
                              size="sm"
                              className="mt-2 h-7 text-xs"
                              onClick={() => handleConnectEmail(account.provider as 'gmail' | 'microsoft')}
                            >
                              <RefreshCw className="h-3 w-3 mr-1" />
                              {isScopeBad ? t('emailAccounts.reconnectWithPermissions') : t('emailAccounts.reconnect')}
                            </Button>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            if (editingSignature === account.id) {
                              setEditingSignature(null)
                            } else {
                              setEditingSignature(account.id)
                              setSignatureDraft(account.signature_html || '')
                            }
                          }}
                        >
                          <Pen className="h-4 w-4 mr-2" />
                          {t('emailAccounts.signature')}
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDisconnectEmail(account.id, account.provider)}
                          disabled={disconnectEmail.isPending}
                        >
                          <Unlink className="h-4 w-4 mr-2" />
                          {t('emailAccounts.disconnect')}
                        </Button>
                      </div>
                    </div>
                    <AccountDailyLimit account={account} />
                    <AccountSenderDomain account={account} />
                    {editingSignature === account.id && (
                      <div className="px-4 pb-4 space-y-2">
                        <Textarea
                          value={signatureDraft}
                          onChange={(e) => setSignatureDraft(e.target.value)}
                          placeholder={t('signature.placeholder')}
                          rows={4}
                          className="text-sm font-mono"
                        />
                        <div className="flex items-center gap-2">
                          <Button
                            size="sm"
                            onClick={() => {
                              updateEmailAccount.mutate(
                                { id: account.id, signature_html: signatureDraft || null },
                                {
                                  onSuccess: () => {
                                    setEditingSignature(null)
                                    toast({ title: t('signature.saved') })
                                  },
                                }
                              )
                            }}
                            disabled={updateEmailAccount.isPending}
                          >
                            {updateEmailAccount.isPending ? (
                              <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                            ) : (
                              <Check className="h-4 w-4 mr-1" />
                            )}
                            {t('signature.save')}
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setEditingSignature(null)}
                          >
                            {t('signature.cancel')}
                          </Button>
                        </div>
                        {signatureDraft && (
                          <div className="border rounded p-3 text-sm">
                            <p className="text-[10px] text-muted-foreground uppercase tracking-wider mb-1">{t('signature.preview')}</p>
                            <div dangerouslySetInnerHTML={{ __html: signatureDraft }} />
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                  )
                })}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground py-4 text-center">
                {t('emailAccounts.empty')}
              </p>
            )}

            <Separator className="my-4" />

            <div className="flex gap-3">
              {/* eslint-disable-next-line i18next/no-literal-string -- provider identifier, not UI copy */}
              {isProviderAvailable('gmail') && (
                <Button variant="outline" onClick={() => handleConnectEmail('gmail')}>
                  <svg className="h-4 w-4 mr-2" viewBox="0 0 24 24">
                    <path
                      fill="currentColor"
                      d="M20 18h-2V9.25L12 13 6 9.25V18H4V6h1.2l6.8 4.25L18.8 6H20m0-2H4c-1.11 0-2 .89-2 2v12a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V6a2 2 0 0 0-2-2Z"
                    />
                  </svg>
                  {t('emailAccounts.connectGmail')}
                </Button>
              )}
              {/* eslint-disable-next-line i18next/no-literal-string -- provider identifier, not UI copy */}
              {isProviderAvailable('microsoft') && (
                <Button variant="outline" onClick={() => handleConnectEmail('microsoft')}>
                  <svg className="h-4 w-4 mr-2" viewBox="0 0 24 24">
                    <path
                      fill="currentColor"
                      d="M2 3h9v9H2V3m9 19H2v-9h9v9M21 3v9h-9V3h9m0 19h-9v-9h9v9Z"
                    />
                  </svg>
                  {t('emailAccounts.connectMicrosoft')}
                </Button>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Team Management */}
        <TeamCard />

        {/* Sending limits of the organization (admin only) */}
        {isAdmin && <SendLimitsCard />}

        {/* HubSpot Connection (admin only) */}
        {isAdmin && <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Link2 className="h-5 w-5" />
              {t('hubspot.title')}
            </CardTitle>
            <CardDescription>
              {t('hubspot.description')}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {loadingHubspot ? (
              <Skeleton className="h-16 w-full" />
            ) : hubspotStatus?.connected ? (
              <div className={`flex items-center justify-between p-4 border rounded-lg ${
                hubspotBad
                  ? 'border-red-500/50 bg-red-950/20'
                  : hubspotScopeBad
                    ? 'border-yellow-500/50 bg-yellow-950/20'
                    : 'bg-green-50 border-green-200 dark:bg-green-950/20 dark:border-green-500/30'
              }`}>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-orange-100 dark:bg-orange-900/30 flex items-center justify-center">
                    <HubSpotIcon className="h-5 w-5 text-orange-600" />
                  </div>
                  <div>
                    <p className="font-medium">
                      {hubspotBad ? t('hubspot.tokenExpired') : t('hubspot.connected')}
                    </p>
                    <p className="text-sm text-muted-foreground">
                      {t('hubspot.portalId', { portalId: hubspotStatus.portal_id })}
                      {health?.hubspot && (
                        <>
                          {' · '}
                          {health.hubspot.status === 'valid' && health.hubspot.scopes_ok && (
                            <span className="text-green-500">{t('hubspot.tokenValid')}</span>
                          )}
                          {hubspotScopeBad && (
                            <span className="text-yellow-500">{t('hubspot.incompletePermissions')}</span>
                          )}
                          {hubspotBad && (
                            <span className="text-red-500">{t('hubspot.reconnectRequired')}</span>
                          )}
                        </>
                      )}
                    </p>
                    {(hubspotBad || hubspotScopeBad) && (
                      <Button
                        variant="destructive"
                        size="sm"
                        className="mt-2 h-7 text-xs"
                        onClick={handleConnectHubSpot}
                      >
                        <RefreshCw className="h-3 w-3 mr-1" />
                        {t('hubspot.reconnect')}
                      </Button>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Button variant="ghost" size="sm" asChild>
                    <a
                      href={`https://app.hubspot.com/contacts/${hubspotStatus.portal_id}`}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <ExternalLink className="h-4 w-4 mr-2" />
                      {t('hubspot.open')}
                    </a>
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={handleDisconnectHubSpot}
                    disabled={disconnectHubspot.isPending}
                  >
                    <Unlink className="h-4 w-4 mr-2" />
                    {t('hubspot.disconnect')}
                  </Button>
                </div>
              </div>
            ) : (
              <div className="text-center py-8">
                <div className="w-16 h-16 rounded-full bg-orange-100 dark:bg-orange-900/30 flex items-center justify-center mx-auto mb-4">
                  <HubSpotIcon className="h-8 w-8 text-orange-600" />
                </div>
                <h3 className="font-medium mb-2">{t('hubspot.notConnected')}</h3>
                <p className="text-sm text-muted-foreground mb-4">
                  {t('hubspot.notConnectedDescription')}
                </p>
                {/* eslint-disable-next-line i18next/no-literal-string -- provider identifier, not UI copy */}
                {isProviderAvailable('hubspot') && (
                  <Button onClick={handleConnectHubSpot}>
                    <Link2 className="h-4 w-4 mr-2" />
                    {t('hubspot.connect')}
                  </Button>
                )}
              </div>
            )}
          </CardContent>
        </Card>}

        {/* Bounces written to HubSpot (admin only, #386) */}
        {isAdmin && hubspotStatus?.connected && (
          <HubSpotBounceReportingCard onGrantPermission={handleConnectHubSpot} />
        )}

        {/* Sync Properties Config (admin only) */}
        {isAdmin && hubspotStatus?.connected && <SyncPropertiesCard />}
      </div>
    </div>
  )
}

function TeamCard() {
  const { t } = useTranslation(['settings', 'common'])
  const { toast } = useToast()
  const userStr = localStorage.getItem('user')
  const user = userStr ? JSON.parse(userStr) : null
  const isAdmin = user?.role === 'admin'

  const { data: members, isLoading: loadingMembers } = useTeamMembers()
  const { data: invitations, isLoading: loadingInvitations } = useInvitations()
  const createInvitation = useCreateInvitation()
  const cancelInvitation = useCancelInvitation()
  const removeMember = useRemoveMember()
  const updateRole = useUpdateMemberRole()
  const [roleChangeTarget, setRoleChangeTarget] = useState<{
    id: string
    name: string | null
    email: string
    currentRole: 'admin' | 'member'
  } | null>(null)

  const handleRoleChange = () => {
    if (!roleChangeTarget) return
    const newRole = roleChangeTarget.currentRole === 'admin' ? 'member' : 'admin'
    updateRole.mutate(
      { userId: roleChangeTarget.id, role: newRole },
      {
        onSuccess: () => {
          const displayName = roleChangeTarget.name || roleChangeTarget.email
          toast({
            title: t('team.roleUpdated'),
            description: t('team.roleUpdatedDesc', {
              name: displayName,
              role: newRole === 'admin' ? t('team.roleAdmin') : t('team.roleMember'),
            }),
          })
          setRoleChangeTarget(null)
        },
        onError: (err) => {
          toast({
            variant: 'destructive',
            title: t('toast.error'),
            description: err.message || t('team.roleChangeFailed'),
          })
          setRoleChangeTarget(null)
        },
      },
    )
  }

  const [inviteOpen, setInviteOpen] = useState(false)
  const [inviteEmail, setInviteEmail] = useState('')
  const [inviteRole, setInviteRole] = useState<string>('member')

  if (!isAdmin) return null

  const handleInvite = () => {
    if (!inviteEmail.trim()) return
    createInvitation.mutate(
      { email: inviteEmail.trim(), role: inviteRole },
      {
        onSuccess: () => {
          toast({ title: t('team.inviteSent'), description: t('team.inviteSentTo', { email: inviteEmail }) })
          setInviteEmail('')
          setInviteRole('member')
          setInviteOpen(false)
        },
        onError: (err) => {
          toast({
            variant: 'destructive',
            title: t('toast.error'),
            description: err.message || t('team.inviteFailed'),
          })
        },
      },
    )
  }

  const handleRemoveMember = (memberId: string, memberEmail: string) => {
    removeMember.mutate(memberId, {
      onSuccess: () => {
        toast({ title: t('team.memberRemoved'), description: t('team.memberRemovedFrom', { email: memberEmail }) })
      },
      onError: (err) => {
        toast({
          variant: 'destructive',
          title: t('toast.error'),
          description: err.message || t('team.memberRemoveFailed'),
        })
      },
    })
  }

  const handleCancelInvitation = (invitationId: string) => {
    cancelInvitation.mutate(invitationId, {
      onSuccess: () => {
        toast({ title: t('team.inviteCancelled') })
      },
      onError: (err) => {
        toast({
          variant: 'destructive',
          title: t('toast.error'),
          description: err.message || t('team.inviteCancelFailed'),
        })
      },
    })
  }

  const pendingInvitations = invitations?.filter((inv) => !inv.accepted_at) ?? []

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Users className="h-5 w-5" />
              {t('team.title')}
            </CardTitle>
            <CardDescription>{t('team.subtitle')}</CardDescription>
          </div>
          <Dialog open={inviteOpen} onOpenChange={setInviteOpen}>
            <DialogTrigger asChild>
              <Button size="sm">
                <UserPlus className="h-4 w-4 mr-2" />
                {t('team.invite')}
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>{t('team.inviteTitle')}</DialogTitle>
                <DialogDescription>
                  {t('team.inviteDescription')}
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <div className="space-y-2">
                  <label className="text-sm font-medium">{t('team.emailLabel')}</label>
                  <Input
                    type="email"
                    placeholder={t('team.emailPlaceholder')}
                    value={inviteEmail}
                    onChange={(e) => setInviteEmail(e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium">{t('team.roleLabel')}</label>
                  <Select value={inviteRole} onValueChange={setInviteRole}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="member">{t('team.roleMember')}</SelectItem>
                      <SelectItem value="admin">{t('team.roleAdmin')}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setInviteOpen(false)}>
                  {t('common:cancel')}
                </Button>
                <Button
                  onClick={handleInvite}
                  disabled={!inviteEmail.trim() || createInvitation.isPending}
                >
                  {createInvitation.isPending && (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  )}
                  {t('team.sendInvite')}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </CardHeader>
      <CardContent>
        {/* Team Members */}
        {loadingMembers ? (
          <div className="space-y-3">
            <Skeleton className="h-14 w-full" />
            <Skeleton className="h-14 w-full" />
          </div>
        ) : members && members.length > 0 ? (
          <div className="space-y-2">
            {members.map((member) => {
              const canChangeRole = isAdmin && member.id !== user?.id
              return (
                <div
                  key={member.id}
                  className="flex items-center justify-between p-3 border rounded-lg"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-full bg-muted flex items-center justify-center">
                      <Mail className="h-4 w-4 text-muted-foreground" />
                    </div>
                    <div>
                      <p className="font-medium text-sm">
                        {member.name || member.email}
                      </p>
                      {member.name && (
                        <p className="text-xs text-muted-foreground">{member.email}</p>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge
                      variant={member.role === 'admin' ? 'default' : 'secondary'}
                      className={
                        canChangeRole
                          ? 'cursor-pointer transition-opacity hover:opacity-70'
                          : ''
                      }
                      onClick={
                        canChangeRole
                          ? () =>
                              setRoleChangeTarget({
                                id: member.id,
                                name: member.name,
                                email: member.email,
                                currentRole: member.role,
                              })
                          : undefined
                      }
                    >
                      {member.role === 'admin' ? t('team.roleAdmin') : t('team.roleMember')}
                    </Badge>
                    {member.id !== user?.id && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleRemoveMember(member.id, member.email)}
                        disabled={removeMember.isPending}
                      >
                        <Trash2 className="h-4 w-4 text-destructive" />
                      </Button>
                    )}
                  </div>
                </div>
              )
            })}
          </div>
        ) : (
          <p className="text-sm text-muted-foreground py-4 text-center">
            {t('team.empty')}
          </p>
        )}

        {/* Pending Invitations */}
        {loadingInvitations ? (
          <div className="mt-4">
            <Skeleton className="h-10 w-full" />
          </div>
        ) : pendingInvitations.length > 0 ? (
          <div className="mt-4">
            <Separator className="mb-3" />
            <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-2">
              {t('team.pendingTitle')}
            </p>
            <div className="space-y-2">
              {pendingInvitations.map((inv) => (
                <div
                  key={inv.id}
                  className="flex items-center justify-between p-3 border rounded-lg border-dashed"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-full bg-muted flex items-center justify-center">
                      <UserPlus className="h-4 w-4 text-muted-foreground" />
                    </div>
                    <div>
                      <p className="font-medium text-sm">{inv.email}</p>
                      <p className="text-xs text-muted-foreground">
                        {t('team.expiresOn', { date: formatDate(inv.expires_at) })}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge variant="outline">
                      {inv.role === 'admin' ? t('team.roleAdmin') : t('team.roleMember')}
                    </Badge>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleCancelInvitation(inv.id)}
                      disabled={cancelInvitation.isPending}
                    >
                      <Trash2 className="h-4 w-4 text-destructive" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ) : null}
      </CardContent>
      <Dialog
        open={roleChangeTarget !== null}
        onOpenChange={(open) => {
          if (!open) setRoleChangeTarget(null)
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('team.changeRoleTitle')}</DialogTitle>
            <DialogDescription>
              {roleChangeTarget?.currentRole === 'member'
                ? t('team.promoteConfirm', { name: roleChangeTarget?.name || roleChangeTarget?.email })
                : t('team.demoteConfirm', { name: roleChangeTarget?.name || roleChangeTarget?.email })}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRoleChangeTarget(null)}>
              {t('common:cancel')}
            </Button>
            <Button onClick={handleRoleChange} disabled={updateRole.isPending}>
              {updateRole.isPending && (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              )}
              {t('common:confirm')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  )
}

function SendLimitsCard() {
  const { t } = useTranslation('settings')
  const { toast } = useToast()
  const { data: organization, isLoading, isError, refetch } = useOrganization()
  const updateOrganization = useUpdateOrganization()

  const handleToggle = (enabled: boolean) => {
    updateOrganization.mutate(
      { contact_daily_limit_enabled: enabled },
      {
        onSuccess: () =>
          toast({
            title: t(enabled ? 'sendLimits.enabled' : 'sendLimits.disabled'),
            description: t(
              enabled ? 'sendLimits.enabledDescription' : 'sendLimits.disabledDescription',
            ),
          }),
        onError: (err: Error) =>
          toast({
            variant: 'destructive',
            title: t('sendLimits.updateFailed'),
            description: err.message,
          }),
      },
    )
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <CalendarClock className="h-5 w-5" />
          {t('sendLimits.title')}
        </CardTitle>
        <CardDescription>{t('sendLimits.description')}</CardDescription>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <Skeleton className="h-12 w-full" />
        ) : isError || !organization ? (
          // No switch at all: one shown off could hide a limit that is on
          <div role="alert" className="flex items-center justify-between gap-3">
            <p className="text-sm text-destructive">{t('sendLimits.loadFailed')}</p>
            <Button variant="outline" size="sm" onClick={() => refetch()}>
              <RefreshCw className="h-3 w-3 mr-1" />
              {t('sendLimits.retry')}
            </Button>
          </div>
        ) : (
          <div className="flex items-start gap-3">
            <Switch
              id="contact-daily-limit"
              className="mt-0.5"
              checked={organization.contact_daily_limit_enabled}
              onCheckedChange={handleToggle}
              disabled={updateOrganization.isPending}
              aria-describedby="contact-daily-limit-hint"
            />
            <div className="space-y-1">
              <Label htmlFor="contact-daily-limit" className="cursor-pointer">
                {t('sendLimits.contactDailyLimit')}
              </Label>
              <p id="contact-daily-limit-hint" className="text-sm text-muted-foreground">
                {t('sendLimits.contactDailyLimitHint')}
              </p>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  )
}

function HubSpotBounceReportingCard({ onGrantPermission }: { onGrantPermission: () => void }) {
  const { t } = useTranslation('settings')
  const { toast } = useToast()
  const { data: organization, isLoading, isError } = useOrganization()
  const updateOrganization = useUpdateOrganization()

  const handleToggle = (enabled: boolean) => {
    updateOrganization.mutate(
      { hubspot_bounce_reporting_enabled: enabled },
      {
        onSuccess: () =>
          toast({
            title: t(enabled ? 'bounceReporting.enabled' : 'bounceReporting.disabled'),
            description: t(
              enabled ? 'bounceReporting.enabledDescription' : 'bounceReporting.disabledDescription',
            ),
          }),
        onError: (err: Error) =>
          toast({
            variant: 'destructive',
            title: t('bounceReporting.updateFailed'),
            description: err.message,
          }),
      },
    )
  }

  // The daily limit card already says when the organization cannot be read
  if (isError) return null
  // An API from before #386 does not have the setting: the switch would look
  // saved without being so
  if (organization && organization.hubspot_bounce_reporting_enabled === undefined) return null

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <MailWarning className="h-5 w-5" />
          {t('bounceReporting.title')}
        </CardTitle>
        <CardDescription>{t('bounceReporting.description')}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        {isLoading || !organization ? (
          <Skeleton className="h-12 w-full" />
        ) : (
          <>
            <div className="flex items-start gap-3">
              <Switch
                id="hubspot-bounce-reporting"
                className="mt-0.5"
                checked={organization.hubspot_bounce_reporting_enabled === true}
                onCheckedChange={handleToggle}
                disabled={updateOrganization.isPending}
                aria-describedby="hubspot-bounce-reporting-hint"
              />
              <div className="space-y-1">
                <Label htmlFor="hubspot-bounce-reporting" className="cursor-pointer">
                  {t('bounceReporting.toggle')}
                </Label>
                <p id="hubspot-bounce-reporting-hint" className="text-sm text-muted-foreground">
                  {t('bounceReporting.hint')}
                </p>
              </div>
            </div>
            {organization.hubspot_bounce_reporting_enabled &&
              !organization.hubspot_bounce_property_permission && (
                <div className="flex items-start gap-2 rounded-md border border-yellow-500/50 bg-yellow-950/10 p-3">
                  <AlertTriangle className="h-4 w-4 mt-0.5 text-yellow-500 shrink-0" />
                  {organization.hubspot_bounce_property_permission_requestable ? (
                    <div className="space-y-2">
                      <p className="text-sm">{t('bounceReporting.permissionMissing')}</p>
                      <Button variant="outline" size="sm" className="h-7 text-xs" onClick={onGrantPermission}>
                        <RefreshCw className="h-3 w-3 mr-1" />
                        {t('bounceReporting.grantPermission')}
                      </Button>
                    </div>
                  ) : (
                    // The HubSpot app cannot grant it yet: reconnecting would not help
                    <p className="text-sm">{t('bounceReporting.permissionUnavailable')}</p>
                  )}
                </div>
              )}
          </>
        )}
      </CardContent>
    </Card>
  )
}

function SyncPropertiesCard() {
  const { t } = useTranslation('settings')
  const { toast } = useToast()
  const { data: config } = useSyncPropertiesConfig()
  const { data: contactProps, isLoading: loadingContact } = useHubSpotAvailableProperties('contacts')
  const { data: companyProps, isLoading: loadingCompany } = useHubSpotAvailableProperties('companies')
  const updateConfig = useUpdateSyncProperties()
  const [searchContact, setSearchContact] = useState('')
  const [searchCompany, setSearchCompany] = useState('')

  const selectedContact = new Set((config?.contact || []).map(p => p.name))
  const selectedCompany = new Set((config?.company || []).map(p => p.name))

  const toggleProperty = (type: 'contact' | 'company', name: string) => {
    const current = type === 'contact' ? selectedContact : selectedCompany
    const newSet = new Set(current)
    if (newSet.has(name)) {
      newSet.delete(name)
    } else {
      newSet.add(name)
    }

    const toItems = (names: Set<string>, props: HubSpotPropertyInfo[] | undefined) =>
      Array.from(names).map(n => {
        const prop = props?.find(p => p.name === n)
        return { name: n, label: prop?.label ?? n.replace(/_/g, ' ') }
      })

    updateConfig.mutate(
      {
        contact: type === 'contact' ? toItems(newSet, contactProps) : toItems(selectedContact, contactProps),
        company: type === 'company' ? toItems(newSet, companyProps) : toItems(selectedCompany, companyProps),
      },
      { onSuccess: () => toast({ title: t('syncProperties.updated') }) }
    )
  }

  const filterProps = (props: HubSpotPropertyInfo[] | undefined, search: string) => {
    if (!props) return []
    if (!search) return props
    const q = search.toLowerCase()
    return props.filter(p => p.label.toLowerCase().includes(q) || p.name.toLowerCase().includes(q))
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <RefreshCw className="h-5 w-5" />
          {t('syncProperties.title')}
        </CardTitle>
        <CardDescription>
          <Trans
            i18nKey="syncProperties.description"
            ns="settings"
            values={{ companyVar: '{{company_brand}}' }}
            components={[
              <code key="prefix" className="text-xs" />,
              <code key="example" className="text-xs" />,
            ]}
          />
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Contact Properties */}
          <div>
            <h4 className="font-medium text-sm mb-2">{t('syncProperties.contactTitle')}</h4>
            <Input
              placeholder={t('syncProperties.searchPlaceholder')}
              value={searchContact}
              onChange={(e) => setSearchContact(e.target.value)}
              className="h-8 text-xs mb-2"
            />
            <div className="border rounded-lg max-h-64 overflow-y-auto">
              {loadingContact ? (
                <div className="p-4"><Skeleton className="h-4 w-full" /></div>
              ) : filterProps(contactProps, searchContact).length === 0 ? (
                <p className="text-xs text-muted-foreground p-3 text-center">{t('syncProperties.noPropertiesFound')}</p>
              ) : (
                filterProps(contactProps, searchContact).map((p) => (
                  <label
                    key={p.name}
                    className="flex items-center gap-2 px-3 py-1.5 hover:bg-accent cursor-pointer text-sm border-b last:border-b-0"
                  >
                    <input
                      type="checkbox"
                      checked={selectedContact.has(p.name)}
                      onChange={() => toggleProperty('contact', p.name)}
                      className="rounded"
                    />
                    <span className="flex-1 truncate">{p.label}</span>
                    <code className="text-[10px] text-muted-foreground">{p.name}</code>
                  </label>
                ))
              )}
            </div>
          </div>

          {/* Company Properties */}
          <div>
            <h4 className="font-medium text-sm mb-2">{t('syncProperties.companyTitle')}</h4>
            <Input
              placeholder={t('syncProperties.searchPlaceholder')}
              value={searchCompany}
              onChange={(e) => setSearchCompany(e.target.value)}
              className="h-8 text-xs mb-2"
            />
            <div className="border rounded-lg max-h-64 overflow-y-auto">
              {loadingCompany ? (
                <div className="p-4"><Skeleton className="h-4 w-full" /></div>
              ) : companyProps === undefined ? (
                <p className="text-xs text-muted-foreground p-3 text-center">
                  {t('syncProperties.scopeMissing')}
                </p>
              ) : filterProps(companyProps, searchCompany).length === 0 ? (
                <p className="text-xs text-muted-foreground p-3 text-center">{t('syncProperties.noPropertiesFound')}</p>
              ) : (
                filterProps(companyProps, searchCompany).map((p) => (
                  <label
                    key={p.name}
                    className="flex items-center gap-2 px-3 py-1.5 hover:bg-accent cursor-pointer text-sm border-b last:border-b-0"
                  >
                    <input
                      type="checkbox"
                      checked={selectedCompany.has(p.name)}
                      onChange={() => toggleProperty('company', p.name)}
                      className="rounded"
                    />
                    <span className="flex-1 truncate">{p.label}</span>
                    <code className="text-[10px] text-muted-foreground">company_{p.name}</code>
                  </label>
                ))
              )}
            </div>
          </div>
        </div>
        <p className="text-xs text-muted-foreground mt-3">
          {t('syncProperties.footerNote')}
        </p>
      </CardContent>
    </Card>
  )
}
